import {
  type ActiveCohortBuilderItemLegacy,
  CohortBuilderDefinitionLegacy,
  DerivedEventLegacy,
  FilterLegacy,
  IndexDateTypeLegacy,
  IndexDateVariableLegacy,
  InputFieldConfig,
  InputFieldTypeLegacy,
  SimpleEventLegacy,
  SimpleEventType,
} from '@verantos-dev/types-cohorts';

import { buildConfigMap, buildIndexDateConfigMap } from '../runtime/utils/buildConfigMap';
import { reduceDerivedConfigMap } from '../runtime/utils/derivedEventUtils';
import {
  DERIVED_EVENT_FILTER_KEYS,
  FILTER_KEYS,
  SIMPLE_EVENT_FILTER_MAPPING,
} from '../runtime/utils/filterTypes';

const getConfigForSimpleEvent = (fieldId: string, simpleEvent: SimpleEventLegacy) => {
  const allConfigMaps = FILTER_KEYS.reduce((acc, key) => {
    const filters = simpleEvent[key] as FilterLegacy[];
    const filterType = SIMPLE_EVENT_FILTER_MAPPING[key];
    filters.forEach((f) => {
      const configMap = buildConfigMap(simpleEvent.type as SimpleEventType, f, filterType, []);
      Object.assign(acc, configMap);
    });
    return acc;
  }, {} as Record<string, InputFieldConfig>);

  return allConfigMaps[fieldId];
};

const getConfigForDerivedEvent = (
  fieldId: string,
  derivedEvent: DerivedEventLegacy,
  simpleEvents: SimpleEventLegacy[],
  derivedEvents: DerivedEventLegacy[]
) => {
  const eventFilterNames = derivedEvent.eventFilters.map(
    (eventFilter) => eventFilter.eventName.value || ''
  );
  const allConfigMaps = DERIVED_EVENT_FILTER_KEYS.reduce(
    (acc, key) =>
      reduceDerivedConfigMap(acc, key, {
        derivedEvent: derivedEvent as unknown as ActiveCohortBuilderItemLegacy,
        eventFilters: derivedEvent.eventFilters,
        simpleEvents,
        derivedEvents,
        eventFilterNames,
      }),
    {} as Record<string, InputFieldConfig>
  );

  return allConfigMaps[fieldId];
};

const getConfigForIndexDate = (
  fieldId: string,
  indexDate: IndexDateVariableLegacy,
  simpleEvents: SimpleEventLegacy[],
  derivedEvents: DerivedEventLegacy[]
) => {
  const configMap = buildIndexDateConfigMap(
    indexDate as unknown as ActiveCohortBuilderItemLegacy,
    simpleEvents,
    derivedEvents
  );
  return configMap[fieldId];
};

const getDefaultFieldValueForSimpleEvent = (
  fieldId: string,
  fieldValue: string | undefined,
  simpleEvent: SimpleEventLegacy
) => {
  const configMap = getConfigForSimpleEvent(fieldId, simpleEvent);
  const options = configMap?.options?.map((o) => o.value) || [];
  if (
    InputFieldTypeLegacy.DROPDOWN === configMap.inputType &&
    options.length > 0 &&
    !options.find((o) => o === fieldValue)
  ) {
    return options[0];
  }
  return fieldValue;
};

const getDefaultFieldValueForDerivedEvent = (
  fieldId: string,
  fieldValue: string | undefined,
  derivedEvent: DerivedEventLegacy,
  simpleEvents: SimpleEventLegacy[],
  derivedEvents: DerivedEventLegacy[]
) => {
  const configMap = getConfigForDerivedEvent(fieldId, derivedEvent, simpleEvents, derivedEvents);
  const options = configMap?.options?.map((o) => o.value) || [];
  if (
    InputFieldTypeLegacy.DROPDOWN === configMap.inputType &&
    options.length > 0 &&
    !options.find((o) => o === fieldValue)
  ) {
    return options[0];
  }
  return fieldValue;
};

const getDefaultFieldValueForIndexDateEvent = (
  fieldId: string,
  fieldValue: string | undefined,
  indexDate: IndexDateVariableLegacy,
  simpleEvents: SimpleEventLegacy[],
  derivedEvents: DerivedEventLegacy[]
) => {
  const configMap = getConfigForIndexDate(fieldId, indexDate, simpleEvents, derivedEvents);
  const options = configMap?.options?.map((o) => o.value) || [];
  if (
    InputFieldTypeLegacy.DROPDOWN === configMap.inputType &&
    options.length > 0 &&
    !options.find((o) => o === fieldValue)
  ) {
    return options[0];
  }
  return fieldValue;
};

export const populateIncorrectFilterOptionValues = (
  cohortDefinition: CohortBuilderDefinitionLegacy
): CohortBuilderDefinitionLegacy => {
  // Defaulting for simple events
  const simpleEvents = cohortDefinition.simpleEvents;

  for (const simpleEvent of simpleEvents) {
    for (const numericFilter of simpleEvent.numericFilters) {
      for (const inputField of numericFilter.inputFields) {
        inputField.value = getDefaultFieldValueForSimpleEvent(
          inputField.id,
          inputField.value,
          simpleEvent
        );
      }
    }

    for (const uniquenessFilter of simpleEvent.uniquenessFilters) {
      for (const inputField of uniquenessFilter.inputFields) {
        inputField.value = getDefaultFieldValueForSimpleEvent(
          inputField.id,
          inputField.value,
          simpleEvent
        );
      }
    }
  }

  // Defaulting for derived events
  const derivedEvents = cohortDefinition.derivedEvents;

  for (const derivedEvent of derivedEvents) {
    for (const eventFilter of derivedEvent.eventFilters) {
      if (eventFilter.eventDateAttribute) {
        eventFilter.eventDateAttribute.value = getDefaultFieldValueForDerivedEvent(
          eventFilter.eventDateAttribute.id,
          eventFilter.eventDateAttribute.value,
          derivedEvent,
          simpleEvents,
          derivedEvents
        );
      }
      if (eventFilter.position) {
        eventFilter.position.value = getDefaultFieldValueForDerivedEvent(
          eventFilter.position.id,
          eventFilter.position.value,
          derivedEvent,
          simpleEvents,
          derivedEvents
        );
      }
    }

    for (const uniquenessFilter of derivedEvent.uniquenessFilters) {
      for (const inputField of uniquenessFilter.inputFields) {
        inputField.value = getDefaultFieldValueForDerivedEvent(
          inputField.id,
          inputField.value,
          derivedEvent,
          simpleEvents,
          derivedEvents
        );
      }
    }

    for (const temporalConstraint of derivedEvent.temporalConstraints) {
      for (const inputField of temporalConstraint.inputFields) {
        inputField.value = getDefaultFieldValueForDerivedEvent(
          inputField.id,
          inputField.value,
          derivedEvent,
          simpleEvents,
          derivedEvents
        );
      }
    }
  }

  // Defaulting for index date
  const indexDate = cohortDefinition.indexDate;
  if (indexDate && indexDate.type === IndexDateTypeLegacy.EVENT && indexDate.indexDateEvent) {
    if (indexDate.indexDateEvent.eventDateAttribute) {
      indexDate.indexDateEvent.eventDateAttribute.value = getDefaultFieldValueForIndexDateEvent(
        indexDate.indexDateEvent.eventDateAttribute.id,
        indexDate.indexDateEvent.eventDateAttribute.value,
        indexDate,
        simpleEvents,
        derivedEvents
      );
    }
    if (indexDate.indexDateEvent.occurrence) {
      indexDate.indexDateEvent.occurrence.value = getDefaultFieldValueForIndexDateEvent(
        indexDate.indexDateEvent.occurrence.id,
        indexDate.indexDateEvent.occurrence.value,
        indexDate,
        simpleEvents,
        derivedEvents
      );
    }
  }

  return {
    ...cohortDefinition,
    simpleEvents: simpleEvents,
    derivedEvents: derivedEvents,
    indexDate: indexDate,
    // FEATURE FLAG: Preserve analytical variables fields when present
    // These fields come from backend when analytical_variables_in_cohort_builder is enabled
    // CLEANUP TODO: When removing the `analytical_variables_in_cohort_builder` feature flag, ensure these fields are always preserved
    analyticalVariables: cohortDefinition.analyticalVariables,
    criteria: cohortDefinition.criteria,
  };
};
